/**
 * Charts, drawn as inline SVG.
 *
 * No charting library: these are three specific shapes, and hand-drawing
 * them keeps the marks on the theme tokens and the labels inside the
 * viewBox. Every scale places its own ticks, and every tick names a value
 * the data actually reaches.
 */

const NUM = new Intl.NumberFormat('en-IN');

/* ── Radial gauge: one rate against a full circle ── */
export function Gauge({ value, label, sub, size = 168 }) {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const stroke = 13;
  const r = (size - stroke) / 2 - 2;
  const c = 2 * Math.PI * r;
  // Three-quarter arc, opening at the bottom.
  const sweep = 0.78;
  const dash = c * sweep;
  const filled = dash * (pct / 100);
  const cx = size / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${pct}% ${label}`}>
      <g transform={`rotate(-234 ${cx} ${cx})`}>
        <circle
          cx={cx} cy={cx} r={r} fill="none"
          stroke="rgba(255,255,255,.12)" strokeWidth={stroke}
          strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
        />
        <circle
          cx={cx} cy={cx} r={r} fill="none"
          stroke="url(#td-gauge)" strokeWidth={stroke}
          strokeDasharray={`${filled} ${c}`} strokeLinecap="round"
        />
      </g>
      <defs>
        <linearGradient id="td-gauge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#4653E3" />
          <stop offset="100%" stopColor="#1D91DA" />
        </linearGradient>
      </defs>
      <text x={cx} y={cx - 4} textAnchor="middle" fill="#fff"
        style={{ font: '800 30px var(--td-display)', letterSpacing: '-.03em' }}>
        {pct}%
      </text>
      <text x={cx} y={cx + 17} textAnchor="middle" fill="#A7B0E8"
        style={{ font: '700 10px var(--td-body)', letterSpacing: '.14em' }}>
        {label}
      </text>
      {sub && (
        <text x={cx} y={cx + 33} textAnchor="middle" fill="#8891C9"
          style={{ font: '600 10px var(--td-body)', letterSpacing: '.1em' }}>
          {sub}
        </text>
      )}
    </svg>
  );
}

/**
 * Weekly attendance line. A week with no session held has rate === null
 * and breaks the line rather than drawing a drop to zero that never
 * happened — the gap is the truth.
 */
export function TrendChart({ data = [], height = 200 }) {
  const W = 620;
  const H = height;
  const pad = { t: 16, r: 54, b: 30, l: 40 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;

  const points = data.map((d, i) => ({
    ...d,
    x: pad.l + (data.length > 1 ? (i / (data.length - 1)) * iw : iw / 2),
    y: d.rate === null ? null : pad.t + ih - (d.rate / 100) * ih,
  }));

  // Break the path wherever a week has no data.
  const segments = [];
  let run = [];
  points.forEach((p) => {
    if (p.y === null) {
      if (run.length) segments.push(run);
      run = [];
    } else run.push(p);
  });
  if (run.length) segments.push(run);

  const last = [...points].reverse().find((p) => p.y !== null);
  const ticks = [0, 25, 50, 75, 100];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="Weekly attendance rate over the last 12 weeks">
      {ticks.map((t) => {
        const y = pad.t + ih - (t / 100) * ih;
        return (
          <g key={t}>
            <line x1={pad.l} y1={y} x2={W - pad.r} y2={y} stroke="var(--td-line)" strokeWidth="1" />
            <text x={pad.l - 8} y={y + 4} textAnchor="end" fill="var(--td-ink-3)"
              style={{ font: '600 10px var(--td-body)' }}>{t}%</text>
          </g>
        );
      })}

      {segments.map((seg, i) => (
        <polyline
          key={i}
          fill="none"
          stroke="var(--td-sky)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
        />
      ))}

      {points.filter((p) => p.y !== null).map((p) => (
        <circle key={p.weekStart} cx={p.x} cy={p.y} r="3" fill="var(--td-sky)" />
      ))}

      {last && (
        <>
          <circle cx={last.x} cy={last.y} r="5" fill="var(--td-sky)" stroke="var(--td-surface)" strokeWidth="2" />
          <text x={last.x + 10} y={last.y + 4} fill="var(--td-sky)"
            style={{ font: '700 12px var(--td-body)' }}>{last.rate}%</text>
        </>
      )}

      {points.map((p, i) =>
        i % 2 === 0 ? (
          <text key={p.weekStart} x={p.x} y={H - 10} textAnchor="middle" fill="var(--td-ink-3)"
            style={{ font: '600 10px var(--td-body)' }}>{p.label}</text>
        ) : null
      )}
    </svg>
  );
}

/* ── Donut with a centred total, paired with its own legend ── */
export function Donut({ data = [], total, caption, size = 190 }) {
  const stroke = 26;
  const r = (size - stroke) / 2 - 2;
  const c = 2 * Math.PI * r;
  const sum = data.reduce((n, d) => n + d.value, 0) || 1;
  const palette = ['var(--td-indigo)', 'var(--td-sky)', 'var(--td-good)', 'var(--td-orange)', 'var(--td-warn)', 'var(--td-bad)'];

  let offset = 0;
  const cx = size / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={caption}>
      <g transform={`rotate(-90 ${cx} ${cx})`}>
        {data.map((d, i) => {
          const len = (d.value / sum) * c;
          const el = (
            <circle
              key={d.name}
              cx={cx} cy={cx} r={r} fill="none"
              stroke={palette[i % palette.length]}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </g>
      <text x={cx} y={cx - 2} textAnchor="middle" fill="var(--td-ink)"
        style={{ font: '800 24px var(--td-display)', letterSpacing: '-.03em' }}>
        {NUM.format(total ?? sum)}
      </text>
      <text x={cx} y={cx + 16} textAnchor="middle" fill="var(--td-ink-2)"
        style={{ font: '700 9.5px var(--td-body)', letterSpacing: '.14em' }}>
        {caption}
      </text>
    </svg>
  );
}

export function DonutLegend({ data = [] }) {
  const sum = data.reduce((n, d) => n + d.value, 0) || 1;
  const palette = ['var(--td-indigo)', 'var(--td-sky)', 'var(--td-good)', 'var(--td-orange)', 'var(--td-warn)', 'var(--td-bad)'];
  return (
    <ul className="td-legend">
      {data.map((d, i) => (
        <li key={d.name}>
          <span className="td-legend-dot" style={{ background: palette[i % palette.length] }} />
          <span className="td-legend-name">{d.name}</span>
          <span className="td-legend-value">{NUM.format(d.value)}</span>
          <span className="td-legend-pct">{Math.round((d.value / sum) * 100)}%</span>
        </li>
      ))}
    </ul>
  );
}

/* ── Sparkline for a table cell ── */
export function Spark({ data = [], width = 68, height = 22 }) {
  const vals = data.filter((v) => v !== null && v !== undefined);
  if (vals.length < 2) return <span className="td-muted">—</span>;
  const max = Math.max(...vals, 1);
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * width},${height - (v / max) * height}`);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline fill="none" stroke="var(--td-sky)" strokeWidth="1.6" strokeLinecap="round" points={pts.join(' ')} />
    </svg>
  );
}
